import { Form, Formik } from 'formik'
import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import * as Yup from 'yup'

import { ButtonWithProgress } from '@Components/Button/ButtonWithProgress'
import { TextField } from '@Components/FormElements/Formik/TextField'
import { MetadataContext } from '@Globals/Metadata'
import { useNewsletterSubscription } from '@Globals/Newsletter/useNewsletterSubscription'
import { useTranslation } from '@Globals/Translations'

interface NewsletterSubscriptionFormProps {
  onSubmit: () => void
}

const FormWrapper = styled(Form)`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  width: 100%;

  .button-container {
    width: 100%;
    margin-bottom: 3px;

    > div {
      margin-bottom: 0;
    }
  }

  .join-button {
    margin-bottom: 0;
  }

  .unsubscribe-text {
    display: block;
    font-size: 0.665rem;
    color: ${({ theme }) => theme.colors.greyLight};

    a {
      color: ${({ theme }) => theme.colors.greyLight};
      text-decoration: underline;
    }
  }
`

export const NewsletterSubscriptionForm = ({
  onSubmit,
}: NewsletterSubscriptionFormProps) => {
  const { t, tDanger } = useTranslation()
  const { cmsData } = useContext(MetadataContext)
  const { loading: isSubscribing, subscribe } = useNewsletterSubscription()

  const NewsletterSignupSchema = useMemo(() => Yup.object().shape({
    email: Yup.string()
      .email(t('validation:YOUR_EMAIL_IS_INVALID'))
      .required(t('validation:EMAIL_REQUIRED_TEXT')),
  }), [t])

  return (
    <Formik
      initialValues={{ email: '' }}
      onSubmit={async ({ email }) => {
        await subscribe({ email, source: 'footer' })
        onSubmit()
      }}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={NewsletterSignupSchema}
    >
      <FormWrapper>
        <TextField
          id="th-footer-newsletter-email"
          label={t('footer:FOOTER_SIGN_UP_YOUR_EMAIL')}
          name="email"
          type="email"
        />
        <div className="button-container">
          <ButtonWithProgress className="join-button" disabled={isSubscribing} loading={isSubscribing} type="submit">
            {t('footer:YES_I_WANT_TO_JOIN')}
          </ButtonWithProgress>
        </div>
        <span
          className="unsubscribe-text"
          {...tDanger('sidebar:TRIBE_PRIVACY_UNSUBSCRIBE', {
            arg1: cmsData?.urls?.privacyPolicy,
          })}
        />
      </FormWrapper>
    </Formik>
  )
}
